import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Footer from './Footer';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import CoverLetterPage from './CoverLetterPage';
import './CoverLetterLanding.css';

const HERO_IMG = '/images/cover-letter/hero-letter-stage.png';
const TONE_IMG = '/images/cover-letter/tone-spectrum.png';
const PERSONALIZE_IMG = '/images/cover-letter/personalize-company.png';
const MULTI_IMG = '/images/cover-letter/multi-version.png';

/* ---------- Three.js calligraphy canvas ---------- */
const InkTrailCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 70;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const makeSprite = (inner, mid) => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, inner);
      g.addColorStop(0.5, mid);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    };
    const inkSprite  = makeSprite('rgba(255,230,200,0.9)', 'rgba(255,150,60,0.45)');
    const dustSprite = makeSprite('rgba(255,255,255,0.7)', 'rgba(224,163,255,0.18)');

    // single calligraphic stroke as a fading trail of points
    const TRAIL_LEN = 220;
    const trailPos   = new Float32Array(TRAIL_LEN * 3);
    const trailAlpha = new Float32Array(TRAIL_LEN);
    const trailSize  = new Float32Array(TRAIL_LEN);
    for (let i = 0; i < TRAIL_LEN; i++) {
      trailAlpha[i] = 0;
      trailSize[i]  = 0;
    }
    const trailGeo = new THREE.BufferGeometry();
    trailGeo.setAttribute('position', new THREE.BufferAttribute(trailPos, 3));
    trailGeo.setAttribute('aAlpha',   new THREE.BufferAttribute(trailAlpha, 1));
    trailGeo.setAttribute('aSize',    new THREE.BufferAttribute(trailSize, 1));

    const trailMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uMap: { value: inkSprite } },
      vertexShader: `
        attribute float aAlpha;
        attribute float aSize;
        varying float vAlpha;
        void main() {
          vAlpha = aAlpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * (300.0 / -mv.z);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: `
        uniform sampler2D uMap;
        varying float vAlpha;
        void main() {
          vec4 t = texture2D(uMap, gl_PointCoord);
          gl_FragColor = vec4(t.rgb, t.a * vAlpha);
        }
      `,
    });
    const trail = new THREE.Points(trailGeo, trailMat);
    scene.add(trail);

    // sparse drifting paper dust
    const DUST_COUNT = 70;
    const dustPos    = new Float32Array(DUST_COUNT * 3);
    const dustSpeed  = new Float32Array(DUST_COUNT);
    const dustDrift  = new Float32Array(DUST_COUNT);
    for (let i = 0; i < DUST_COUNT; i++) {
      dustPos[i * 3]     = (Math.random() - 0.5) * 180;
      dustPos[i * 3 + 1] = (Math.random() - 0.5) * 110;
      dustPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      dustSpeed[i] = 0.015 + Math.random() * 0.04;
      dustDrift[i] = Math.random() * Math.PI * 2;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      size: 0.9, map: dustSprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.32,
    });
    const dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    // calligraphy stroke is parameterized along t and stamps a new head each frame
    // Lissajous-with-rotation evokes flowing handwriting without obvious looping
    const stroke = (s) => {
      const a = 38, b = 18;
      const x = a * Math.sin(s * 1.0 + Math.sin(s * 0.27) * 0.6);
      const y = b * Math.sin(s * 1.7 + Math.cos(s * 0.19) * 0.9);
      const z = 6 * Math.sin(s * 0.5);
      // slow plane rotation so the stroke explores different baselines
      const r = s * 0.08;
      const cs = Math.cos(r), sn = Math.sin(r);
      return [x * cs - y * sn, x * sn + y * cs, z];
    };

    let raf;
    let mx = 0, my = 0, t = 0, head = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.012;

      // stamp new head and fade existing trail
      const [hx, hy, hz] = stroke(t);
      trailPos[head * 3]     = hx;
      trailPos[head * 3 + 1] = hy;
      trailPos[head * 3 + 2] = hz;
      trailAlpha[head] = 1.0;
      trailSize[head]  = 2.6;
      head = (head + 1) % TRAIL_LEN;
      // gentle decay so the tail tapers smoothly
      for (let i = 0; i < TRAIL_LEN; i++) {
        trailAlpha[i] *= 0.972;
        trailSize[i]  *= 0.992;
      }
      trailGeo.attributes.position.needsUpdate = true;
      trailGeo.attributes.aAlpha.needsUpdate = true;
      trailGeo.attributes.aSize.needsUpdate = true;

      // dust slow upward drift with a touch of horizontal sway
      const dp = dustGeo.attributes.position.array;
      for (let i = 0; i < DUST_COUNT; i++) {
        dp[i * 3]     += Math.sin(t + dustDrift[i]) * 0.02;
        dp[i * 3 + 1] += dustSpeed[i];
        if (dp[i * 3 + 1] > 55) dp[i * 3 + 1] = -55;
      }
      dustGeo.attributes.position.needsUpdate = true;

      camera.position.x += (mx * 4 - camera.position.x) * 0.03;
      camera.position.y += (-my * 3 - camera.position.y) * 0.03;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      trailGeo.dispose(); trailMat.dispose();
      dustGeo.dispose();  dustMat.dispose();
      inkSprite.dispose(); dustSprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="cll-hero-canvas" aria-hidden="true" />;
};

/* ---------- Letter typewriter demo ---------- */
const LETTER_INTRO = 'Dear Hiring Manager at Stripe,';
const LETTER_BODY = 'When I read your Backend Engineer posting yesterday, the line about "obsessive ownership of latency" stopped me cold — that\'s exactly what cut Acme\'s checkout p95 by 38% under my lead last quarter.';

const LetterTypewriterDemo = () => {
  const ref = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [intro, setIntro] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && phase === 'idle') setPhase('intro'); }),
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'intro') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setIntro(LETTER_INTRO.slice(0, i));
      if (i >= LETTER_INTRO.length) { clearInterval(id); setTimeout(() => setPhase('body'), 350); }
    }, 35);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'body') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setBody(LETTER_BODY.slice(0, i));
      if (i >= LETTER_BODY.length) { clearInterval(id); setPhase('done'); }
    }, 16);
    return () => clearInterval(id);
  }, [phase]);

  return (
    <section className="cll-section" ref={ref}>
      <div className="cll-section-grid cll-flip">
        <div className="cll-letter-card">
          <div className="cll-letter-paper">
            <p className="cll-letter-intro">
              {intro}
              {phase === 'intro' && <span className="cll-caret">|</span>}
            </p>
            {(phase === 'body' || phase === 'done') && (
              <p className="cll-letter-body">
                {body}
                {phase === 'body' && <span className="cll-caret">|</span>}
              </p>
            )}
            {phase === 'done' && (
              <div className="cll-letter-meta">
                <span>STRIPE-specific opener</span>
                <span>Quantified hook (38%)</span>
                <span>Tone: confident-warm</span>
              </div>
            )}
          </div>
        </div>

        <div className="cll-section-text">
          <span className="cll-eyebrow">FROM BLANK PAGE TO POLISHED LETTER</span>
          <h2 className="cll-h2">
            Watch the AI compose your letter — <span className="cll-grad-text">JD-aware, personal, in your voice</span>.
          </h2>
          <p className="cll-lead">
            No template plug-and-play. The AI reads the JD, picks the line that should hook the
            recruiter, ties it back to a real metric from your resume, and writes the rest of the
            letter around that opening.
          </p>
          <ul className="cll-bullet-list">
            <li><span className="cll-dot" />Company-specific opener (no "I am writing to apply for…")</li>
            <li><span className="cll-dot" />Quantified hook from your resume</li>
            <li><span className="cll-dot" />Tone matched to the role's seniority</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ---------- Tone spectrum demo ---------- */
const TONES = [
  { id: 'formal', label: 'Formal', hue: '#00ffff' },
  { id: 'professional', label: 'Professional', hue: '#e0a3ff' },
  { id: 'warm', label: 'Warm', hue: '#ffd166' },
  { id: 'friendly', label: 'Friendly', hue: '#ff8c00' },
];
const TONE_SAMPLES = {
  formal: 'I am writing to express my strong interest in the Backend Engineer position at your organization.',
  professional: 'I\'m excited about the Backend Engineer role at Stripe — your obsession with latency aligns deeply with my last 18 months of work.',
  warm: 'When I saw the Backend Engineer posting at Stripe, I genuinely lit up — this team gets latency in a way most don\'t.',
  friendly: 'Hi! I just had to apply for the Backend Engineer role — Stripe\'s post about latency hit different, and I\'d love to talk about how I\'d add to that team.',
};

const ToneSpectrumDemo = () => {
  const [tone, setTone] = useState('professional');
  return (
    <section className="cll-section">
      <div className="cll-section-grid">
        <div className="cll-section-text">
          <span className="cll-eyebrow">TONE CALIBRATION</span>
          <h2 className="cll-h2">
            Pick the voice that <span className="cll-grad-text">matches the room</span>.
          </h2>
          <p className="cll-lead">
            Big-bank legal team? Formal. Series-B startup with emoji in the JD? Warm. Pick a tone
            from the spectrum and the entire letter rewrites instantly — opening, hook, sign-off
            and all.
          </p>
        </div>

        <div className="cll-tone-card">
          <div className="cll-tone-pills">
            {TONES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`cll-tone-pill ${tone === t.id ? 'active' : ''}`}
                style={{ '--accent': t.hue }}
                onClick={() => setTone(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="cll-tone-sample">
            <span className="cll-tone-sample-label">Sample · {TONES.find((x) => x.id === tone).label}</span>
            <p>"{TONE_SAMPLES[tone]}"</p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Drop the JD', body: 'Paste the role description — or just role title + company name.' },
  { title: 'Add your resume', body: 'PDF / DOCX or "Use my profile" — the source of every claim in the letter.' },
  { title: 'Pick your tone', body: 'Formal, professional, warm, or friendly. The entire letter recalibrates.' },
  { title: 'AI composes', body: 'Company-specific opener, quantified hook, body matched to JD requirements.' },
  { title: 'Edit inline', body: 'Tweak any phrase — the AI rewrites surrounding sentences for cohesion.' },
  { title: 'Export', body: 'Download as DOCX, PDF, or copy plain text. Your letter, your way.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="cll-section" ref={ref}>
      <div className="cll-section-head">
        <span className="cll-eyebrow">HOW IT WORKS</span>
        <h2 className="cll-h2">
          Six steps. <span className="cll-grad-text">A polished letter in 90 seconds.</span>
        </h2>
        <p className="cll-lead cll-center">
          From blank page to polished, JD-tailored letter — faster than reading the JD twice.
        </p>
      </div>
      <div className="cll-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`cll-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="cll-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`cll-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 90, suffix: 's', label: 'From JD to letter' },
  { val: 4, suffix: '', label: 'Tone presets' },
  { val: 2, suffix: '×', label: 'Higher response rate' },
  { val: 350, suffix: 'K+', label: 'Letters generated' },
];
const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400; const t0 = performance.now(); let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="cll-stat">
      <div className="cll-stat-num">{v}{stat.suffix}</div>
      <div className="cll-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="cll-stats-band" ref={ref}>
      <div className="cll-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Does it sound like AI wrote it?', a: 'No — and that\'s the design goal. The AI uses your resume\'s real phrases and metrics, calibrates tone to the role, and avoids the giveaways ("I am writing to express my keen interest…"). Recruiters consistently rate the letters as human-written.' },
  { q: 'Will my letter look the same as everyone else\'s?', a: 'Never. Every letter is generated from your specific resume + the specific JD. Two people applying to the same role would get completely different letters because their resumes, hooks, and metrics differ.' },
  { q: 'Can I edit the letter after it\'s generated?', a: 'Yes. The output is fully editable inline. You can rewrite any sentence, ask the AI to redo a paragraph in a different tone, or hand-tune the final draft.' },
  { q: 'What formats can I export?', a: 'DOCX, PDF, and plain text. Most ATS-friendly is DOCX or plain text — pasted directly into the application form.' },
  { q: 'Does it work without a resume?', a: 'It can — but the letter is much weaker without one. With a resume, the AI grounds every claim in real experience. Without, it has to stay generic.' },
  { q: 'How is my data handled?', a: 'Your resume and JD are processed in-stream and not used to train models. Letters you generate are saved to your dashboard so you can revisit them, but you can delete any time.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="cll-section">
      <div className="cll-section-head">
        <span className="cll-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="cll-h2">Everything you wanted to ask</h2>
      </div>
      <div className="cll-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`cll-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="cll-faq-q">
              <span>{f.q}</span>
              <span className="cll-faq-chev">+</span>
            </div>
            <div className="cll-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const CoverLetterLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  const goStudio = useCallback(() => {
    if (studioRef.current) studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => { if (show) navigate('/?openLogin=1'); }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="cll-page">
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="cll-hero" ref={heroRef}>
        <InkTrailCanvas />
        <div className="cll-hero-bg" />
        <img src={HERO_IMG} alt="" className="cll-hero-img" loading="eager" />

        <div className="cll-hero-content">
          <span className="cll-hero-pill">
            <span className="cll-pill-dot" /> 90 seconds · 4 tones · Recruiter-tested
          </span>
          <h1 className="cll-h1">
            A cover letter <span className="cll-grad-text">they'll actually read</span>.
          </h1>
          <p className="cll-hero-sub">
            Drop the JD. Drop your resume. Pick a tone. Get back a letter that opens with a
            company-specific hook, ties to a real metric from your resume, and sounds like you —
            not like ChatGPT.
          </p>

          <div className="cll-hero-cta">
            <button className="cll-btn cll-btn-primary" onClick={goStudio}>
              <span>Write my cover letter</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="cll-btn cll-btn-ghost" onClick={() => navigate('/resume-builder')}>
              Build resume first
            </button>
          </div>

          <div className="cll-hero-checks">
            <span>✓ Company-specific opener</span>
            <span>✓ Quantified hook from your resume</span>
            <span>✓ DOCX / PDF / plain-text export</span>
          </div>
        </div>

        {/* Floating glass letter card */}
        <div className="cll-floater" aria-hidden="true">
          <div className="cll-floater-card">
            <div className="cll-floater-head">
              <span className="cll-floater-dot" />
              <span>DRAFT · 1 of 5 variants</span>
            </div>
            <p className="cll-floater-line cll-floater-bold">Dear Hiring Team at Linear,</p>
            <p className="cll-floater-line">When I read your engineering principles…</p>
            <div className="cll-floater-bar w90" />
            <div className="cll-floater-bar w70" />
            <div className="cll-floater-bar w85" />
            <div className="cll-floater-bar w50" />
          </div>
          <div className="cll-floater-glow" />
        </div>
      </section>

      <section className="cll-studio" ref={studioRef} id="studio">
        <div className="cll-studio-head">
          <span className="cll-eyebrow">COVER LETTER STUDIO</span>
          <h2 className="cll-h2">
            Write it now — <span className="cll-grad-text">live, in this page</span>.
          </h2>
          <p className="cll-lead cll-center">
            Drop the JD, link your resume, generate. Edit inline, export as DOCX/PDF/text.
          </p>
        </div>
        <CoverLetterPage embedded />
      </section>

      <LetterTypewriterDemo />
      <ToneSpectrumDemo />

      <section className="cll-section">
        <div className="cll-section-grid">
          <div className="cll-section-text">
            <span className="cll-eyebrow">PER-COMPANY PERSONALIZATION</span>
            <h2 className="cll-h2">
              Every letter pulls in the <span className="cll-grad-text">company's actual story</span>.
            </h2>
            <p className="cll-lead">
              The AI doesn't just slot the company name into a template. It reads the JD for
              cultural cues, recent product mentions, and the team's stated values — then weaves
              one or two of those into the opening so the letter couldn't have been sent to anyone
              else.
            </p>
            <ul className="cll-bullet-list">
              <li><span className="cll-dot" />Picks up cues from the JD's language</li>
              <li><span className="cll-dot" />Avoids generic praise ("amazing company")</li>
              <li><span className="cll-dot" />Anchors to something specific they said</li>
            </ul>
          </div>
          <div className="cll-img-frame">
            <img src={PERSONALIZE_IMG} alt="Per-company personalization" />
            <div className="cll-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="cll-section">
        <div className="cll-section-grid cll-flip">
          <div className="cll-img-frame">
            <img src={TONE_IMG} alt="Tone spectrum" />
            <div className="cll-img-frame-glow" />
          </div>
          <div className="cll-section-text">
            <span className="cll-eyebrow">FOUR TONE PRESETS</span>
            <h2 className="cll-h2">
              <span className="cll-grad-text">Formal to friendly</span> — one click.
            </h2>
            <p className="cll-lead">
              Different rooms need different voices. Pick from formal, professional, warm, or
              friendly — and the entire letter recalibrates: opening, body, sign-off, even the
              greeting style.
            </p>
          </div>
        </div>
      </section>

      <section className="cll-section">
        <div className="cll-section-grid">
          <div className="cll-section-text">
            <span className="cll-eyebrow">MULTIPLE VARIANTS</span>
            <h2 className="cll-h2">
              Generate <span className="cll-grad-text">five A/B variants</span> in one click.
            </h2>
            <p className="cll-lead">
              Not sure which hook lands best? Generate five takes on the same JD — each with a
              different angle, opener, or emphasis — and pick the one that sounds most like you.
            </p>
          </div>
          <div className="cll-img-frame">
            <img src={MULTI_IMG} alt="Multiple variants" />
            <div className="cll-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="cll-cta-final">
        <div className="cll-cta-card">
          <div className="cll-cta-glow" />
          <h2 className="cll-h2">
            Stop hand-writing every cover letter. <span className="cll-grad-text">Compose smarter</span>.
          </h2>
          <p>90 seconds. JD-aware. Recruiter-tested. Your voice.</p>
          <div className="cll-hero-cta center">
            <button className="cll-btn cll-btn-primary" onClick={goStudio}>
              <span>Write my cover letter</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="cll-btn cll-btn-ghost" onClick={() => navigate('/resume-builder')}>Build resume first</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoverLetterLanding;
